import { statusColors } from '@/constants'
import { Customer, Order, SharedProps } from '@/types'
import { formatName, formatStatus } from '@/utils/format'
import { Head, Link, router } from '@inertiajs/react'
import { Badge, Box, Button, Group, Paper, Stack, Table, Text, Title } from '@mantine/core'
import { IconArrowLeft, IconPencil, IconShoppingCart } from '@tabler/icons-react'
import dayjs from 'dayjs'

interface CustomerShowPageProps extends SharedProps {
  customer: Customer
  orders: Order[]
}

export default function CustomerShowPage({ customer, orders, current_organization }: CustomerShowPageProps) {
  return (
    <>
      <Head title={`Client: ${formatName(customer)} - ${current_organization?.name}`} />

      <Stack gap="xl">
        <Group justify="space-between">
          <Button
            component={Link}
            href={`/organizations/${current_organization?.slug}/customers`}
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            color="gray"
          >
            Retour aux clients
          </Button>

          <Button
            component={Link}
            href={`/organizations/${current_organization?.slug}/customers/${customer.id}/edit`}
            variant="light"
            leftSection={<IconPencil size={16} />}
          >
            Modifier le client
          </Button>
        </Group>

        <Paper withBorder radius="md" p="xl">
          <Stack gap="xs">
            <Group justify="space-between" align="start">
              <Stack gap={0}>
                <Title order={2}>{formatName(customer)}</Title>
              </Stack>
            </Group>

            <Group gap="xl" mt="md">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Téléphone
                </Text>
                <Text size="lg">{customer.phone_number}</Text>
              </div>

              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Email
                </Text>
                <Text size="lg">{customer.email || '-'}</Text>
              </div>
            </Group>

            {customer.address && (
              <Box mt="md">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Adresse
                </Text>
                <Text size="lg">{customer.address}</Text>
              </Box>
            )}
          </Stack>
        </Paper>

        <Stack gap="md">
          <Title order={3}>Historique des commandes</Title>

          {orders.length > 0 ? (
            <Paper withBorder radius="md" p="md">
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>N° Commande</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Statut</Table.Th>
                    <Table.Th align="right">Prix Total</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orders.map((order) => (
                    <Table.Tr
                      key={order.id}
                      onClick={() => router.get(`/organizations/${current_organization?.slug}/orders/${order.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>#{order.id}</Table.Td>
                      <Table.Td>{dayjs(order.created_at).format('DD/MM/YYYY')}</Table.Td>
                      <Table.Td>
                        <Badge color={statusColors[order.status]} variant="light">
                          {formatStatus(order)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={700}>{Number(order.price).toFixed(2)}€</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          ) : (
            <Paper
              py={60}
              px="md"
              radius="lg"
              style={{
                border: '1px dashed var(--mantine-color-gray-3)',
                textAlign: 'center',
                backgroundColor: 'transparent',
              }}
            >
              <Stack align="center">
                <IconShoppingCart size={40} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed">Aucune commande pour ce client</Text>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Stack>
    </>
  )
}
